import { gsap } from "@client/gsap";
import { useGSAP } from "@gsap/react";
import React, { useRef } from "react";
import { t } from "ttag";

import {
    ServiceDetailSubBlock,
    ServiceDetailsBlock as Value,
} from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";

import { RichTextBlock } from "../RichText";
import { Svg } from "../Svg";

import styles from "./ServiceDetailsBlock.module.scss";

const ServicePanel = ({
    value,
    index,
}: {
    value: ServiceDetailSubBlock;
    index: number;
}) => (
    <div className={styles.panel} data-svc={index}>
        <div className={styles.panelBg}>
            <div className={styles.panelBgInner}>
                {value.background_image && (
                    <ImageChooserBlock
                        value={value.background_image}
                        attrs={{ className: styles.panelBgPicture }}
                        imageAttrs={{ className: styles.panelBgImage }}
                    />
                )}
            </div>
        </div>
        <div className={styles.content}>
            <h2 className={styles.title}>{value.title}</h2>
            {value.description && (
                <div className={styles.description}>
                    <RichTextBlock value={value.description} />
                </div>
            )}
            <div className={styles.capabilities}>
                {value.capabilities?.map((cap) => (
                    <span key={cap.id} className={styles.pill}>
                        {cap.value}
                    </span>
                ))}
            </div>
        </div>
        {value.case_study_name && (
            <div className={styles.caseStudy}>
                <span className={styles.eyebrow}>{t`Case Study`}</span>
                <span className={styles.clientName}>
                    {value.case_study_name}
                </span>
                {value.case_study_description && (
                    <p className={styles.caseStudyDescription}>
                        {value.case_study_description}
                    </p>
                )}
                {value.case_study_link?.url && (
                    <a href={value.case_study_link.url} className={styles.cta}>
                        <span>{value.case_study_link.display_name}</span>
                        <Svg
                            name="arrow-right"
                            visuallyHiddenText={null}
                            aria-hidden={true}
                        />
                    </a>
                )}
            </div>
        )}
    </div>
);

export const ServiceDetailsBlock = (props: Value) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const viewportRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        const container = containerRef.current;
        const viewport = viewportRef.current;
        if (!container || !viewport) return;

        const panels = viewport.querySelectorAll<HTMLElement>(
            `.${styles.panel}`,
        );
        if (panels.length < 2) return;

        const h2 = viewport.querySelector<HTMLElement>(
            `.${styles.introTitle} h2`,
        );
        const thread = viewport.querySelector<HTMLElement>(`.${styles.thread}`);

        if (h2 && !h2.querySelector(`.${styles.siLine}`)) {
            const italic = h2.querySelector("em, i");
            const firstText = h2.firstChild?.textContent?.trim() ?? "";

            h2.textContent = "";

            const line1 = document.createElement("span");
            line1.className = styles.siLine;
            const inner1 = document.createElement("span");
            inner1.className = styles.siInner;
            inner1.textContent = firstText;
            line1.appendChild(inner1);
            h2.appendChild(line1);

            if (italic) {
                const line2 = document.createElement("span");
                line2.className = `${styles.siLine} ${styles.siLineAccent}`;
                const inner2 = document.createElement("span");
                inner2.className = styles.siInner;
                inner2.textContent = italic.textContent ?? "";
                line2.appendChild(inner2);
                h2.appendChild(line2);
            }
        }

        const mm = gsap.matchMedia();
        mm.add("(min-width: 1024px)", () => {
            const n = panels.length;
            const compressedGap = 22;

            panels.forEach((panel, i) => {
                gsap.set(panel, { yPercent: i * 100 });
            });

            // Intro heading line reveal
            const siInners = viewport.querySelectorAll<HTMLElement>(
                `.${styles.siInner}`,
            );

            if (siInners.length > 0) {
                gsap.set(siInners, { yPercent: 120 });
                if (thread) gsap.set(thread, { scaleY: 0 });

                const introTl = gsap.timeline({
                    scrollTrigger: {
                        trigger: panels[0],
                        start: "center 88%",
                        toggleActions: "play none none none",
                    },
                });

                introTl.to(
                    siInners[0],
                    { yPercent: 0, ease: "power4.out", duration: 0.7 },
                    0,
                );
                if (siInners[1]) {
                    introTl.to(
                        siInners[1],
                        { yPercent: 0, ease: "power4.out", duration: 0.7 },
                        0.2,
                    );
                }
                if (thread) {
                    introTl.to(
                        thread,
                        { scaleY: 1, ease: "power2.out", duration: 1.15 },
                        0.6,
                    );
                }
            }

            // Panel slide animation
            const positionPanels = (p: number) => {
                const active = Math.min(Math.floor(p), n - 1);
                const frac = p - active;
                for (let i = 0; i < n; i++) {
                    let y: number;
                    if (i <= active) {
                        y =
                            -(active - i) * compressedGap -
                            frac * compressedGap;
                    } else if (i === active + 1) {
                        y = (1 - frac) * 100;
                    } else {
                        y = (1 - frac) * 100 + (i - active - 1) * 100;
                    }
                    gsap.set(panels[i], { yPercent: y });
                }
            };

            const scrollDistance = 0.6 + 1.2 * (n - 1);
            const progress = { p: 0 };

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: container,
                    start: "top top",
                    end: `+=${Math.round(scrollDistance * 100)}%`,
                    pin: viewport,
                    scrub: 1,
                },
            });

            tl.to(progress, {
                p: 1,
                duration: 0.6,
                ease: "power1.inOut",
                onUpdate: () => positionPanels(progress.p),
            });

            for (let i = 1; i < n - 1; i++) {
                tl.to(progress, {
                    p: i + 1,
                    duration: 1.2,
                    ease: "power1.inOut",
                    onUpdate: () => positionPanels(progress.p),
                });
            }
        });
    }, []);

    return (
        <div ref={containerRef} className={styles.container}>
            <div ref={viewportRef} className={styles.viewport}>
                <div
                    className={`${styles.panel} ${styles.introPanel}`}
                    data-svc="0"
                >
                    <div className={styles.panelBg}>
                        <div className={styles.panelBgInner} />
                    </div>
                    <div className={styles.introContent}>
                        <div className={styles.introHeading}>
                            {props.header && (
                                <RichTextBlock
                                    className={styles.introTitle}
                                    value={props.header}
                                />
                            )}
                            <span
                                className={styles.thread}
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                </div>
                {props.services?.map((block, i) => (
                    <ServicePanel
                        key={block.id}
                        value={block.value}
                        index={i + 1}
                    />
                ))}
            </div>
        </div>
    );
};
